import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { logout, me, portfolio } from '../lib/api.ts';
import { fmtSignedHKD } from '../lib/format.ts';
import { AddCardModal } from './AddCardModal.tsx';
import { Icon } from './Icon.tsx';

interface TopBarProps {
  live?: boolean;
  cardCount?: number;
  lastSync?: string;
  fx?: number;
  /** Which top-level screen we're on. Drives the nav pill highlight. */
  view?: 'dashboard' | 'holdings' | 'detail';
  /** Pre-computed P&L for the pill. If omitted, TopBar fetches portfolio itself. */
  pnlHkd?: number;
}

export function TopBar({
  live = true,
  cardCount = 0,
  lastSync = '—',
  fx = 7.81,
  view = 'dashboard',
  pnlHkd,
}: TopBarProps) {
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);
  const meQ = useQuery({ queryKey: ['me'], queryFn: me, staleTime: 60_000, retry: false });
  // Always reach for portfolio so the P&L pill stays warm across screens.
  // The Holdings page passes pnlHkd explicitly to avoid double-fetch flicker.
  const portfolioQ = useQuery({
    queryKey: ['portfolio', 'real'],
    queryFn: () => portfolio('real'),
    enabled: pnlHkd === undefined,
    staleTime: 30_000,
  });
  const pnl = pnlHkd ?? portfolioQ.data?.totalHkd ?? 0;
  const pnlPos = pnl >= 0;

  const user = meQ.data?.user;
  const handle = user?.handle ?? '—';
  const role = user?.role ?? 'guest';
  const initials = handle.slice(0, 2).toUpperCase();

  async function onLogout() {
    try {
      await logout();
    } catch {
      /* even if it fails, force back to login */
    }
    navigate('/login', { replace: true });
  }

  return (
    <div className="topbar">
      <div className="brand">
        {live && <span className="brand-dot" />}
        pokequant
        <span className="brand-meta">v0.0.1 · hk</span>
      </div>

      <nav className="topbar-nav" aria-label="Primary">
        <Link
          to="/"
          className={`nav-pill ${view === 'dashboard' || view === 'detail' ? 'on' : ''}`}
        >
          Dashboard
        </Link>
        <Link to="/holdings" className={`nav-pill ${view === 'holdings' ? 'on' : ''}`}>
          Holdings
        </Link>
        <button
          type="button"
          className="nav-pill nav-pill-add"
          onClick={() => setShowAdd(true)}
          title="Add a card by Snkrdunk URL"
        >
          + Add card
        </button>
      </nav>

      <div className="topbar-status">
        <span className={`statpill pnl-pill ${pnl === 0 ? '' : pnlPos ? 'pos' : 'neg'}`}>
          <span className="k">P&L</span>
          <span className="v mono">{fmtSignedHKD(pnl)}</span>
        </span>
        <span className="statpill live">
          <span className="k">live</span>
          <span className="v">snkrdunk</span>
        </span>
        <span className="statpill">
          <span className="k">cards</span>
          <span className="v">{cardCount}</span>
        </span>
        <span className="statpill">
          <span className="k">last sync</span>
          <span className="v">{lastSync}</span>
        </span>
        <span className="statpill">
          <span className="k">fx</span>
          <span className="v">USD/HKD {fx.toFixed(2)}</span>
        </span>
        <button
          type="button"
          className="user-tab"
          onClick={onLogout}
          title={`${handle} · sign out`}
          aria-label="Sign out"
        >
          <span className="avatar">{initials}</span>
          <span>{role}</span>
          <Icon name="close" size={10} />
        </button>
      </div>
      {showAdd && <AddCardModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}

export interface Filter {
  label: string;
  value?: number | string;
  active?: boolean;
  tone?: 'buy' | 'sell' | 'hold' | 'arb';
  /** True for filters on a different axis than momentum. SubBar inserts a
   *  divider before the first axis-separator chip so the operator reads
   *  "different lens" rather than "another momentum bucket". */
  axisSep?: boolean;
  onClick?: () => void;
}

export type SortKey =
  | 'price-desc'
  | 'price-asc'
  | 'pop-desc'
  | 'pop-asc'
  | 'score-desc'
  | 'd1-desc'
  | 'd1-asc'
  | 'd30-desc'
  | 'd30-asc'
  | 'name-asc'
  | 'spread-desc';

export const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'price-desc', label: 'Price ↓' },
  { value: 'price-asc', label: 'Price ↑' },
  { value: 'pop-desc', label: 'Population ↓' },
  { value: 'pop-asc', label: 'Population ↑' },
  { value: 'score-desc', label: 'Signal score ↓' },
  { value: 'd1-desc', label: '24h change ↓' },
  { value: 'd1-asc', label: '24h change ↑' },
  { value: 'd30-desc', label: '30d change ↓' },
  { value: 'd30-asc', label: '30d change ↑' },
  { value: 'spread-desc', label: 'Spread % ↓' },
  { value: 'name-asc', label: 'Name A→Z' },
];

interface SubBarProps {
  filters: Filter[];
  count: number;
  sort: SortKey;
  onSortChange: (s: SortKey) => void;
  totalCards?: number;
  query: string;
  onQueryChange: (q: string) => void;
  priceMin: string;
  onPriceMinChange: (s: string) => void;
  priceMax: string;
  onPriceMaxChange: (s: string) => void;
}

export function SubBar({
  filters,
  count,
  sort,
  onSortChange,
  totalCards = 0,
  query,
  onQueryChange,
  priceMin,
  onPriceMinChange,
  priceMax,
  onPriceMaxChange,
}: SubBarProps) {
  return (
    <div className="subbar">
      <div className="searchbox">
        <Icon name="search" />
        <input
          className="searchbox-input"
          type="search"
          placeholder={`Search ${totalCards} cards…`}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          aria-label="Search cards by name, set, or number"
        />
        {query && (
          <button
            type="button"
            className="searchbox-clear"
            onClick={() => onQueryChange('')}
            aria-label="Clear search"
          >
            <Icon name="close" size={12} />
          </button>
        )}
      </div>
      <div className="price-range" aria-label="Price filter">
        <span className="price-range-label">$</span>
        <input
          className="price-input"
          type="number"
          min="0"
          step="1"
          placeholder="min"
          value={priceMin}
          onChange={(e) => onPriceMinChange(e.target.value)}
          aria-label="Minimum price USD"
        />
        <span className="price-range-sep">–</span>
        <input
          className="price-input"
          type="number"
          min="0"
          step="1"
          placeholder="max"
          value={priceMax}
          onChange={(e) => onPriceMaxChange(e.target.value)}
          aria-label="Maximum price USD"
        />
      </div>
      {filters.map((f) => (
        <span key={f.label} style={{ display: 'contents' }}>
          {f.axisSep && <span className="chip-axis-sep" aria-hidden="true" />}
          <button
            type="button"
            onClick={f.onClick}
            className={`chip ${f.active ? 'chip-active' : ''} ${f.tone ? `chip-${f.tone}` : ''}`}
            style={{ cursor: f.onClick ? 'pointer' : 'default' }}
          >
            {f.label}
            {f.value !== undefined && <span className="ck">{f.value}</span>}
          </button>
        </span>
      ))}
      <div className="subbar-right">
        <label className="sort-select" aria-label="Sort cards">
          <Icon name="sort" size={12} />
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as SortKey)}
            className="sort-select-control"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <span className="chip-sep" />
        <span className="subbar-count">{count}</span>
        <span style={{ color: 'var(--ink-mute)' }}>visible</span>
      </div>
    </div>
  );
}
